import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// Usar o mesmo domínio que a página está sendo servida
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';

const FundadorDashboard = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [indicados, setIndicados] = useState([]);
  const [convites, setConvites] = useState([]);
  const [taxas, setTaxas] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [newLink, setNewLink] = useState(null);

  useEffect(() => {
    loadDashboard();
    loadConvites();
  }, []);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/fundador/dashboard`, { headers });
      
      setStats(response.data.stats);
      setIndicados(response.data.indicados);
      setTaxas(response.data.taxas);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadConvites = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/fundador/meus-convites`, { headers });
      setConvites(response.data.convites || []);
    } catch (error) {
      console.error('Erro ao carregar convites:', error);
    }
  };

  const gerarNovoConvite = async () => {
    setGeneratingLink(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.post(`${API}/fundador/gerar-convite`, {}, { headers });
      
      setNewLink(response.data);
      toast.success('Link de convite gerado!');
      loadConvites();
    } catch (error) {
      console.error('Erro ao gerar convite:', error);
      toast.error('Erro ao gerar link de convite');
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copiado!');
  };

  const deletarConvite = async (token) => {
    if (!window.confirm('Tem certeza que deseja deletar este convite?')) {
      return;
    }
    
    try {
      const tokenAuth = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${tokenAuth}` };
      
      await axios.delete(`${API}/fundador/convite/${token}`, { headers });
      
      toast.success('Convite deletado com sucesso!');
      loadConvites();
    } catch (error) {
      console.error('Erro ao deletar convite:', error);
      toast.error(error.response?.data?.detail || 'Erro ao deletar convite');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#D4AF37] flex items-center gap-2">
            💎 Dashboard de Fundador
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Acompanhe seus ganhos e indicados
          </p>
        </div>
        <button
          onClick={gerarNovoConvite}
          disabled={generatingLink}
          className="px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#B8962D] text-black font-bold rounded-xl hover:shadow-lg hover:shadow-[#D4AF37]/30 transition-all disabled:opacity-50"
        >
          {generatingLink ? '⏳ Gerando...' : '🔗 Gerar Link de Convite'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#1A1A1A] border border-[#D4AF37]/30 rounded-xl p-4">
          <div className="text-2xl lg:text-3xl font-bold text-[#D4AF37]">
            {formatCurrency(stats?.total_ganhos)}
          </div>
          <div className="text-sm text-gray-400">💰 Total de Ganhos</div>
        </div>
        <div className="bg-[#1A1A1A] border border-green-500/30 rounded-xl p-4">
          <div className="text-2xl lg:text-3xl font-bold text-green-400">
            {formatCurrency(stats?.ganhos_criador)}
          </div>
          <div className="text-sm text-gray-400">🎬 Ganhos como Criador</div>
        </div>
        <div className="bg-[#1A1A1A] border border-purple-500/30 rounded-xl p-4">
          <div className="text-2xl lg:text-3xl font-bold text-purple-400">
            {formatCurrency(stats?.ganhos_comissao)}
          </div>
          <div className="text-sm text-gray-400">🔗 Comissão de Indicados</div>
        </div>
        <div className="bg-[#1A1A1A] border border-blue-500/30 rounded-xl p-4">
          <div className="text-2xl lg:text-3xl font-bold text-blue-400">
            {stats?.total_indicados || 0}
          </div>
          <div className="text-sm text-gray-400">👥 Criadores Indicados</div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#2E003E]/20 border border-[#D4AF37]/30 rounded-xl p-4">
        <div className="flex flex-wrap gap-6 text-sm">
          <div>
            <span className="text-gray-400">Sua taxa:</span>
            <span className="text-[#D4AF37] font-bold ml-2">{taxas?.fundador_rate}</span>
          </div>
          <div>
            <span className="text-gray-400">Taxa Lyberhot:</span>
            <span className="text-gray-300 ml-2">{taxas?.lyberhot_fee}</span>
          </div>
          <div>
            <span className="text-gray-400">Comissão sobre indicados:</span>
            <span className="text-purple-400 font-bold ml-2">{taxas?.comissao_indicados} para sempre</span>
          </div>
        </div>
      </div>

      {/* New Link Modal */}
      {newLink && (
        <div className="bg-[#1A1A1A] border border-green-500/30 rounded-xl p-6">
          <h3 className="text-green-400 font-bold mb-4 flex items-center gap-2">
            ✨ Novo Link de Convite Gerado!
          </h3>
          <div className="flex items-center gap-2 mb-4">
            <input
              type="text"
              value={newLink.invite_url}
              readOnly
              className="flex-1 bg-[#0D0D0D] border border-[#333] rounded-lg px-3 py-2 text-white text-sm"
            />
            <button
              onClick={() => copyLink(newLink.invite_url)}
              className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg font-semibold hover:bg-[#B8962D] transition-all"
            >
              📋 Copiar
            </button>
          </div>
          <p className="text-gray-400 text-sm">
            Envie este link para novos criadores. Você receberá <strong className="text-purple-400">5% de comissão</strong> sobre tudo que eles faturarem!
          </p>
          <button
            onClick={() => setNewLink(null)}
            className="mt-4 text-gray-500 hover:text-white text-sm"
          >
            Fechar
          </button>
        </div>
      )}

      {/* Indicados Section */}
      <div className="bg-[#1A1A1A] border border-[#D4AF37]/20 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-[#D4AF37]/20 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">👥 Meus Indicados</h3>
          <span className="text-sm text-gray-400">
            {stats?.indicados_ativos || 0} ativos de {stats?.total_indicados || 0}
          </span>
        </div>
        
        {indicados.length === 0 ? (
          <div className="p-8 text-center">
            <span className="text-5xl block mb-4">🔗</span>
            <h4 className="text-white font-semibold mb-2">Nenhum indicado ainda</h4>
            <p className="text-gray-400 text-sm mb-4">
              Gere um link de convite e compartilhe com novos criadores para começar a ganhar comissões!
            </p>
            <button
              onClick={gerarNovoConvite}
              disabled={generatingLink}
              className="px-6 py-2 bg-[#D4AF37] text-black rounded-lg font-semibold hover:bg-[#B8962D] transition-all"
            >
              Gerar Primeiro Convite
            </button>
          </div>
        ) : (
          <div className="divide-y divide-[#333]">
            {indicados.map((indicado) => (
              <div key={indicado.id} className="p-4 flex items-center justify-between hover:bg-[#0D0D0D] transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#2E003E] flex items-center justify-center overflow-hidden">
                    {indicado.picture ? (
                      <img src={indicado.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xl">👤</span>
                    )}
                  </div>
                  <div>
                    <p className="text-white font-semibold">{indicado.nome}</p>
                    <p className="text-gray-400 text-sm">@{indicado.username}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[#D4AF37] font-bold">{formatCurrency(indicado.comissao_gerada)}</p>
                  <p className="text-gray-500 text-xs">
                    Faturou: {formatCurrency(indicado.total_faturado)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Convites Section */}
      <div className="bg-[#1A1A1A] border border-[#D4AF37]/20 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-[#D4AF37]/20 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">🔗 Meus Convites</h3>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-green-400">
              ✅ {convites.filter(c => c.used).length} ativados
            </span>
            <span className="text-yellow-400">
              ⏳ {convites.filter(c => !c.used).length} pendentes
            </span>
          </div>
        </div>
        
        {convites.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <p>Você ainda não gerou nenhum convite.</p>
          </div>
        ) : (
          <div className="divide-y divide-[#333] max-h-80 overflow-y-auto">
            {convites.map((convite) => (
              <div key={convite.id} className="p-4 flex items-center justify-between hover:bg-[#0D0D0D] transition-colors">
                <div className="flex items-center gap-3 flex-1">
                  <span className={`w-3 h-3 rounded-full flex-shrink-0 ${convite.used ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`}></span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-mono text-sm truncate">{convite.token}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        convite.used 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {convite.status_label || (convite.used ? '✅ Ativado' : '⏳ Aguardando')}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs mt-1">
                      {convite.used && convite.indicado_info ? (
                        <span className="text-green-400">
                          Perfil criado: <strong>{convite.indicado_info.name}</strong> (@{convite.indicado_info.username})
                        </span>
                      ) : (
                        <span>Link aguardando ser utilizado</span>
                      )}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  {!convite.used && (
                    <>
                      <button
                        onClick={() => copyLink(`${window.location.origin}/convite/${convite.token}`)}
                        className="px-3 py-1.5 bg-[#333] hover:bg-[#444] rounded text-sm text-gray-300 transition-colors"
                        title="Copiar link"
                      >
                        📋 Copiar
                      </button>
                      <button
                        onClick={() => deletarConvite(convite.token)}
                        className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 rounded text-sm text-red-400 transition-colors"
                        title="Deletar convite"
                      >
                        🗑️
                      </button>
                    </>
                  )}
                  {convite.used && (
                    <span className="text-green-400 text-sm">
                      ✅ Ativo
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FundadorDashboard;
